import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager, NoteBlockTab } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { Dataset, DataTableSchema } from 'src/app/services/models/datasets.models';
import { Subject, takeUntil, forkJoin, debounceTime, switchMap, EMPTY } from 'rxjs';

interface SelectOption {
  value: any;
  label: string;
}

@Component({
  selector: 'bhub-note-block-cell',
  standalone: false,
  templateUrl: './note-block-cell.component.html',
  styleUrl: './note-block-cell.component.scss'
})
export class NoteBlockCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();
  
  // UI Properties
  labelText: string = '';
  noteValue: string = '';
  placeholder: string = '';
  
  // Configuration properties
  dataSource: 'context' | 'dataset' | 'none' = 'none';
  contextObjectKey: string = '';
  fieldName: string = '';
  datasetId: number | null = null;

  // Tabs feature
  enableTabs: boolean = false;
  tabs: NoteBlockTab[] = [];
  activeTabIndex: number = 0;
  
  // State
  private destroy$ = new Subject<void>();
  
  constructor(
    private datasetService: DatasetService,
    private dynamicTableService: DynamicTableService
  ) {
    super();
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  protected initializeCell(): void {
    this.updateFieldConfiguration();
    this.initializeContext(); // Ensure context exists
    this.loadInitialValue();
  }
  
  private initializeContext(): void {
    if (!this.viewManager) return;
    
    // Initialize context data if needed
    if (!this.viewManager.contextData) {
      this.viewManager.contextData = {};
    }
    
    // Initialize context for Note Block cells that manage their own context
    if (this.dataSource === 'context' && this.contextObjectKey) {
      // Initialize the context key if it doesn't exist
      if (!this.viewManager.contextData.hasOwnProperty(this.contextObjectKey)) {
        // Initialize with empty object that can hold field values
        this.viewManager.contextData[this.contextObjectKey] = {};
      }
    }
    
    // Initialize context variable for independent fields
    if (this.dataSource === 'none') {
      const contextVariable = this.getConfigValue('contextVariable', '');
      if (contextVariable) {
        // Store current note value in the context variable
        this.viewManager.contextData[contextVariable] = this.noteValue || '';
      }
    }
  }
  
  protected onCellOrDataChange(): void {
    this.updateFieldConfiguration();
    this.initializeContext(); // Ensure context exists
    this.loadInitialValue();
  }
  
  protected onCellUpdate(): void {
    this.updateFieldConfiguration();
    this.initializeContext(); // Ensure context exists
    this.loadInitialValue();
  }
  
  protected onDataSet(data: any): void {
    // Update value when external data changes
    this.loadInitialValue();
  }
  
  private updateFieldConfiguration(): void {
    if (!this.cell) return;

    // Get basic configuration
    this.labelText = this.getConfigValue('label', '');
    this.placeholder = this.getConfigValue('placeholder', 'Enter your notes...');

    // Determine data source
    this.dataSource = this.getConfigValue('dataSource', 'none') as 'context' | 'dataset' | 'none';

    // Get data binding configuration
    this.contextObjectKey = this.getDataBindingValue('dataObjectKey') || '';
    this.fieldName = this.getDataBindingValue('fieldName') || '';
    this.datasetId = this.getDataBindingValue('datasetId') || null;

    // Tabs — only honored in 'context' data source mode (tabs map to fields on a context object)
    this.enableTabs = this.dataSource === 'context' && !!this.getConfigValue('noteBlockEnableTabs', false);
    const rawTabs = this.getConfigValue<NoteBlockTab[]>('noteBlockTabs', []) || [];
    this.tabs = this.enableTabs ? rawTabs.filter(t => !!t.fieldName) : [];
    if (this.activeTabIndex >= this.tabs.length) {
      this.activeTabIndex = 0;
    }
  }

  /** True when tabs are enabled and at least one tab is configured with a field. */
  get hasTabs(): boolean {
    return this.enableTabs && this.tabs.length > 0;
  }

  /** Field name the textarea is currently editing — either the active tab's field or the single-field binding. */
  private get currentFieldName(): string {
    if (this.hasTabs) {
      return this.tabs[this.activeTabIndex]?.fieldName || '';
    }
    return this.fieldName;
  }

  selectTab(index: number): void {
    if (index < 0 || index >= this.tabs.length) return;
    this.activeTabIndex = index;
    this.loadInitialValue();
  }
  
  private loadInitialValue(): void {
    const field = this.currentFieldName;
    if (this.dataSource === 'context' && this.contextObjectKey && field && this.viewManager) {
      const dataObject = this.viewManager.contextData[this.contextObjectKey];
      if (dataObject) {
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          this.noteValue = dataObject[0][field] || '';
        } else if (typeof dataObject === 'object' && dataObject !== null) {
          this.noteValue = dataObject[field] || '';
        } else {
          this.noteValue = '';
        }
      } else {
        this.noteValue = '';
      }
    } else if (this.dataSource === 'dataset' && this.datasetId && this.fieldName) {
      // For dataset source, we might want to load data from the dataset
      // This would typically be handled by loading data from the service
      this.noteValue = '';
    } else {
      // Independent field - value is managed locally
      this.noteValue = this.getConfigValue('defaultValue', '');
    }
  }
  
  onNoteChange(): void {
    const field = this.currentFieldName;
    // Update the view manager context if configured
    if (this.dataSource === 'context' && this.contextObjectKey && field && this.viewManager) {
      const dataObject = this.viewManager.contextData[this.contextObjectKey];

      if (dataObject) {
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          dataObject[0][field] = this.noteValue;
        } else if (typeof dataObject === 'object' && dataObject !== null) {
          dataObject[field] = this.noteValue;
        }
        // Note: We don't emit contextDataChange here because:
        // 1. The context object is already updated in-place
        // 2. All cells share the same viewManager reference
        // 3. Emitting would trigger unnecessary refreshes/API calls
        // The Action Button will read the updated context when saving
      }
    } else if (this.dataSource === 'dataset') {
      // For dataset source, no emit needed - data will be saved by Action Button
    } else {
      // For independent fields, update context variable if configured
      const contextVariable = this.getConfigValue('contextVariable', '');
      if (contextVariable && this.viewManager) {
        if (!this.viewManager.contextData) {
          this.viewManager.contextData = {};
        }
        this.viewManager.contextData[contextVariable] = this.noteValue;
      }
      // Note: No emit needed - context is updated in-place
    }
  }
}